import { Amplify, type ResourcesConfig } from "aws-amplify";
import {
	resetPassword as awsResetPassword,
	signIn as awsSignIn,
	signOut as awsSignOut,
	confirmResetPassword,
	confirmSignIn,
	fetchAuthSession,
	getCurrentUser,
} from "aws-amplify/auth";
import { onMounted, ref } from "vue";
import { emptyGUID } from "./general";

export type FlightPathUser = {
	email: string;
	username: string;
	accountId: string;
	emailVerified: boolean;
	groups: string[];
};

export async function getUserFromIdToken(): Promise<FlightPathUser> {
	const session = await fetchAuthSession();
	const idToken = session.tokens?.idToken?.payload;
	if (!idToken) throw new Error("No id token found");

	return {
		email: idToken.email as string,
		username: idToken["cognito:username"] as string,
		accountId: idToken["custom:accountid"] as string,
		emailVerified: idToken.email_verified as boolean,
		groups: idToken["cognito:groups"] as string[],
	};
}

export function configureAWS(config: ResourcesConfig) {
	Amplify.configure(config);
}

export async function getToken(type: "id" | "access" = "id"): Promise<string> {
	const { tokens } = await fetchAuthSession();
	return type === "id"
		? (tokens?.idToken?.toString() ?? "")
		: (tokens?.accessToken?.toString() ?? "");
}

export async function SignedIn(): Promise<boolean> {
	try {
		const user = await getCurrentUser();
		return user != undefined;
	} catch (error) {
		console.log("Not logged in");
		return false;
	}
}

export function useAuth() {
	const user = ref<FlightPathUser | null>(null);

	async function signIn(
		username: string,
		password: string,
	): Promise<{
		user?: FlightPathUser;
		completeNewPassword?: (
			password: string,
			apiUrl: string,
		) => Promise<void>;
	}> {
		try {
			const result = await awsSignIn({ username, password });
			if (
				result.nextStep.signInStep ===
				"CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
			) {
				return {
					completeNewPassword: async (password, apiUrl) => {
						await confirmSignIn({
							challengeResponse: password,
                            options: {
                                clientMetadata: {
                                    "action" : "FIRST_LOGIN"
                                }
                            }
						});
						const authSession = await fetchAuthSession();
						const token = authSession.tokens?.idToken?.toString();
						if (!token) {
							throw new Error("No id token found");
						}
						const response = await fetch(
							`${apiUrl}/attach-iot-policy`,
							{
								method: "POST",
								headers: {
									Authorization: "Bearer " + token,
								},
								body: authSession.identityId,
							},
						);
						if (!response.ok) {
							throw new Error(
								`Failed to attach IoT policy: ${response.status}`,
							);
						}
						user.value = await getUserFromIdToken();
					},
				};
			} else {
				user.value = await getUserFromIdToken();
				return { user: user.value };
			}
		} catch (error) {
			console.error(`Sign in error: ${error}`);
			throw error;
		}
	}

	async function signOut() {
		try {
			await awsSignOut();
			user.value = null;
		} catch (error) {
			console.log("error signing out: ", error);
		}
	}

	async function forgotPassword(username: string) {
		try {
			await awsResetPassword({ username });
			return async (confirmationCode: string, newPassword: string) => {
				try {
					await confirmResetPassword({
						username,
						confirmationCode,
						newPassword,
					});
				} catch (error) {
					console.error(error);
					throw error;
				}
			};
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	async function resetPassword(
		username: string,
		confirmationCode: string,
		newPassword: string,
	) {
		try {
			await confirmResetPassword({
				username,
				confirmationCode,
				newPassword,
			});
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	onMounted(async () => {
		try {
			user.value = await getUserFromIdToken();
		} catch (error) {
			console.log("Not logged in");
		}
	});

	return { signIn, signOut, user, forgotPassword, resetPassword };
}
const customerId = ref(emptyGUID);
const originalCustomerId = ref(emptyGUID);

export async function setCustomerId(isLocal: boolean) {
	if (!isLocal) {
		try {
			const session = await fetchAuthSession();
			if (!session.tokens?.idToken) return false;
			originalCustomerId.value = customerId.value = session.tokens
				?.idToken?.payload["custom:accountid"] as string;
			customerId.value = originalCustomerId.value;
		} catch {
			return false;
		}
	}

	return true;
}

export async function setOrganizationId(organizationId: string) {
	try {
		const session = await fetchAuthSession();
		if (!session.tokens?.idToken) return false;
		originalCustomerId.value = customerId.value = session.tokens?.idToken
			?.payload["custom:accountid"] as string;
		customerId.value = organizationId;
	} catch {
		return false;
	}

	return true;
}

export function useCustomerId() {
	return customerId.value;
}

export function useCustomerIdRef() {
	return customerId;
}

export function useOriginalCustomerId() {
	return originalCustomerId.value;
}

export function useOriginalCustomerIdRef() {
	return originalCustomerId;
}

export async function isUserFlightPathAdmin(): Promise<boolean> {
	try {
		const session = await fetchAuthSession();
		if (!session.tokens?.idToken) return false;
		const idToken = session.tokens?.idToken?.payload;
		const groups = idToken["cognito:groups"] as string[];
		return groups.includes("FlightPathAdmin");
	} catch (error) {
		console.error(error);
		return false;
	}
}

export async function isUserGroupAuthorized(
	authorizedGroups: string[],
): Promise<boolean> {
	try {
		const session = await fetchAuthSession();
		if (!session.tokens?.idToken) return false;

		const idToken = session.tokens.idToken.payload;
		const groups = idToken["cognito:groups"] as string[];

		return groups.some(group => authorizedGroups.includes(group));
	} catch (error) {
		console.error(error);
		return false;
	}
}

export async function isUserGroupsAuthorized(
	...authorizedGroups: string[][] // Rest parameter for arrays of strings
): Promise<boolean> {
	// Flatten the authorizedGroups array of arrays into a single array
	const flattenedAuthorizedGroups = authorizedGroups.flat();

	// Use the isUserGroupAuthorized function with the flattened array
	return isUserGroupAuthorized(flattenedAuthorizedGroups);
}
